import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExportContext } from '../../contexts/ExportContext';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

const ExportStatusBannerComponent: React.FC = () => {
  const { activeExports, cancelExport, resetAll } = useExportContext();
  const { tr } = useTranslationLgs();
  const [searchParams] = useSearchParams();
  const currentRunAs = searchParams.get('runAs');

  // Only show exports started in the current impersonation context; hide while away (e.g. admin pages)
  const visibleExports = Object.fromEntries(
    Object.entries(activeExports).filter(([, state]) => (state.runAs ?? null) === (currentRunAs ?? null)),
  );

  // If no exports for the current context, don't show banner
  if (Object.keys(visibleExports).length === 0) {
    return null;
  }


  const renderExportItem = (jobId: string, state: (typeof activeExports)[string]) => {
    const { uiStatus, processedCount, totalCount, entityLabel, errorMessage } = state;

    switch (uiStatus) {
      case 'starting':
        return (
          <div key={jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
            <span className="inline-block w-4 h-4 border-2 border-gray-200 rounded-full border-t-[var(--ringil3-brand-color)] animate-spin flex-shrink-0" />
            <span className="text-sm font-medium flex-1">{tr('export.status.preparing', 'Preparing {{label}} export…', { label: entityLabel })}</span>
          </div>
        );

      case 'polling': {
        const pct = totalCount ? Math.min(100, Math.round((processedCount / totalCount) * 100)) : null;
        return (
          <div key={jobId} className="p-3 border-b border-gray-200 last:border-b-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-sm font-medium flex-1">{tr('export.status.exporting', 'Exporting {{label}}…', { label: entityLabel })}</span>
              <RingilButton label={tr('export.status.cancel', 'Cancel')} mode={'text-only'} onClick={() => cancelExport(jobId)} />
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded overflow-hidden mb-1">
              {pct != null
                ? <div className="h-full bg-[var(--ringil3-brand-color)] transition-all" style={{ width: `${pct}%` }} />
                : <div className="h-full bg-[var(--ringil3-brand-color)] animate-pulse w-full" />}
            </div>
            <span className="text-xs text-gray-500">
              {totalCount != null
                ? tr('export.status.progress', '{{processed}} / {{total}}', { processed: processedCount, total: totalCount })
                : tr('export.status.progress_indeterminate', 'Processing…')}
            </span>
          </div>
        );
      }

      case 'ready':
        return (
          <div key={jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
            <span className="text-sm font-medium text-[color:var(--ringil3-success-color,#16a34a)]">
              {tr('export.status.ready', '✓ {{label}} export ready — downloading…', { label: entityLabel })}
            </span>
          </div>
        );

      case 'error':
        return (
          <div key={jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
            <span className="text-sm font-medium text-[color:var(--ringil3-error-color,#ef4444)]">
              {tr('export.status.failed', '✗ {{label}} export failed: {{msg}}', { label: entityLabel, msg: errorMessage ?? '' })}
            </span>
          </div>
        );

      case 'cancelled':
        return (
          <div key={jobId} className="flex items-center gap-2 p-3 border-b border-gray-200 last:border-b-0">
            <span className="text-sm text-gray-600">
              {tr('export.status.cancelled', '{{label}} export cancelled', { label: entityLabel })}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 max-w-md z-50">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <span className="font-semibold text-gray-800">{tr('export.status.title', 'Export Progress')}</span>
        <button
          onClick={resetAll}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {Object.entries(visibleExports).map(([jobId, state]) => renderExportItem(jobId, state))}
      </div>
    </div>
  );
};

export default ExportStatusBannerComponent;
